import { catchError } from "@opendash/core";
import { makeAutoObservable, runInAction } from "mobx";
import Parse from "parse";
import { TicketData } from "../..";

export type FetchObject = {
  filterBy?: {
    junction?: "and" | "or";
    filter: FilterParams[];
  };
  sortBy?: string;
  limit?: number;
  skip?: number;
  order?: "asc" | "desc";
};

export type FetchParams = {
  junction: "and" | "or";
  config: Array<FetchObject>;
};

export type FetchResponse = {
  data: TicketData[];
  total: number;
  success: boolean;
  error: any;
};

export type FilterParams = {
  attribute:
    | "objectId"
    | "state"
    | "issuecategory"
    | "user"
    | "role"
    | "userRole"
    | "createdUser"
    | "source"
    | "project"
    | "title";
  /**
   * the objectId of the attribute to filter by
   */
  value: string;
};

export type TicketManagerConfig = {
  pagination?: boolean;
  filters?: Record<string, FilterParams>;
  sortBy?: string;
  order?: "asc" | "desc";
  filterJunction?: "and" | "or";
  ticketsPerPage?: number;
};

export class TicketManager {
  data: TicketData[] = [];
  totalCount: number = 0;
  loading: boolean = false;

  private pagination: boolean = false;
  private page: number = 0;
  private ticketsPerPage: number = 2;
  private sortBy?: string = undefined;
  private order: "asc" | "desc" = "desc";
  private filters: Map<string, FilterParams> = new Map();
  private filterJunction: "and" | "or" = "and";

  constructor(config: TicketManagerConfig) {
    this.setConfig(config);
    makeAutoObservable(this);
  }

  private get fetchParams(): FetchObject {
    // if pagination is enabled, we only want to fetch the tickets for the current page
    // otherwise we want to fetch all tickets up to the current page. this is used for the "load more" functionality
    const limit = this.pagination
      ? this.ticketsPerPage
      : this.ticketsPerPage * this.currentPage;
    const skip = this.pagination ? this.page * this.ticketsPerPage : 0;

    // creating the filterBy object
    let filterBy: FetchObject["filterBy"] = {
      junction: this.filterJunction,
      filter: [...this.filters.values()]
        .filter((filter) => !!filter && !!filter.value)
        .map((filter) => {
          return { attribute: filter.attribute, value: filter.value };
        }),
    };

    return {
      limit,
      skip,
      sortBy: this.sortBy,
      order: this.order,
      filterBy,
    };
  }

  get hasFilterActive() {
    return this.filters.size > 0;
  }
  /**
   * The total number of pages for the current tickets with the current active filter
   */
  get totalPages() {
    return Math.ceil(this.totalCount / this.ticketsPerPage);
  }

  get currentPage() {
    return this.page + 1;
  }

  incrementPage() {
    runInAction(() => {
      this.page += 1;
    });
  }

  resetPage() {
    runInAction(() => {
      this.page = 0;
    });
  }

  getSortBy() {
    return this.sortBy;
  }

  setConfig(config: TicketManagerConfig) {
    runInAction(() => {
      this.data = [];
      this.totalCount = 0;
      this.pagination = config?.pagination ?? false;
      this.sortBy = config?.sortBy ?? undefined;
      this.order = config?.order ?? "desc";
      this.filterJunction = config?.filterJunction ?? "and";
      this.filters.clear();
      for (const [key, value] of Object.entries(config?.filters ?? {})) {
        this.filters.set(key, value);
      }
      this.ticketsPerPage = config?.ticketsPerPage ?? 5;
    });
  }

  setSortBy(sortBy: string) {
    runInAction(() => {
      this.sortBy = sortBy;
    });
  }

  setOrder(order: "asc" | "desc") {
    runInAction(() => {
      this.order = order;
    });
  }

  getOrder() {
    return this.order;
  }

  toggleOrder() {
    this.setOrder(this.order === "asc" ? "desc" : "asc");
  }

  setFilterJunction(junction: "and" | "or") {
    runInAction(() => {
      this.filterJunction = junction;
    });
  }

  addFilter(key: string, value: FilterParams) {
    runInAction(() => {
      this.filters.set(key, value);
    });
  }

  getFilter(key: string) {
    return this.filters.get(key);
  }

  getFilters() {
    return Array.from(this.filters);
  }

  removeFilter(key: string) {
    runInAction(() => {
      this.filters.delete(key);
    });
  }

  resetFilters() {
    runInAction(() => {
      this.filters.clear();
    });
  }

  /**
   * Fetches the tickets with the current configuration and sets the current data and total count
   * @returns the respone from the fetchTickets function
   */
  async fetchTickets() {
    runInAction(() => {
      this.loading = true;
    });

    const response = await fetchTickets({
      config: [this.fetchParams],
      junction: "or", // irrelevant for a single manager
    });

    runInAction(() => {
      this.data = response.data;
      this.totalCount = response.total;
      this.loading = false;
    });
    return response;
  }

  /**
   * Fetches the tickets for all managers and returns only the tickets that are in all managers
   * IMPORTANT: The sorting of the tickets is applied to all tickets but only the first manager's sorting is used
   * @param manager all managers to fetch the tickets from
   * @returns all tickets that are in all managers
   */
  static async And(...manager: TicketManager[]) {
    const fetchObjects: FetchObject[] = manager.map((m) => m.fetchParams);
    manager.forEach((m) => runInAction(() => (m.loading = true)));
    const response = await fetchTickets({
      config: fetchObjects,
      junction: "and",
    });
    manager.forEach((m) => runInAction(() => (m.loading = false)));
    return response;
  }

  /**
   * Fetches the tickets of all managers and returns all tickets that are in at least one manager. Remove duplicates.
   * IMPORTANT: The sorting and pagination of the tickets is applied to all tickets but only the first manager's sorting/pagination is used
   * @param manager all managers to fetch the tickets from
   * @returns the response containing the total count and tickets
   */
  static async Or(...manager: TicketManager[]) {
    const fetchObjects: FetchObject[] = manager.map((m) => m.fetchParams);
    manager.forEach((m) => runInAction(() => (m.loading = true)));
    const response = await fetchTickets({
      config: fetchObjects,
      junction: "or",
    });
    manager.forEach((m) => runInAction(() => (m.loading = false)));
    return response;

    // return all tickets that are in at least one manager but remove duplicates
    // return Core.Helpers.Array.toDistinct(tickets, (obj) => obj.ticket.id);
  }

  static async Get(ticketId: string): Promise<TicketData | undefined> {
    const response = await new TicketManager({
      filters: { id: { value: ticketId, attribute: "objectId" } },
    }).fetchTickets();

    return response?.data?.[0];
  }
}

/**
 * Fetches tickets from the Parse server
 * @param params the params to filter and sort the tickets and paginate them
 * @returns the response containing the paginated tickets and the total count before pagination
 */
export async function fetchTickets(
  params: FetchParams
): Promise<FetchResponse> {
  const [error, response] = await catchError(
    Parse.Cloud.run("openinc-openservice-ticket-data", {
      config: params.config,
      junction: params.junction,
    })
  );

  if (error || response.success === false) {
    console.log("Error fetching tickets", error ? error : response.error);
    return { data: [], total: 0, success: false, error };
  }
  return response;
}
